import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as OccasiaLogoIcon } from '../icons/occasia-logo-icon.svg';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../Header/constants';
import ProfileNavigation from '../Profile/ProfileNavigation';
import LogoutConfirmModal from '../Header/LogoutConfirmModal';
import messageThreadsApiService from '../../services/messageThreadsApi';
import {
  buildReadMapFromThreads,
  getThreadReadMap,
  hasThreadReadMap,
  countUnreadThreads,
  threadListHasServerUnread,
  setThreadReadMap,
} from '../../utils/messageReadTracker';
import { IconArrowDown, IconBookings, IconHome, IconNotification, IconSearch } from './OrganizerIcons';
import './OrganizerDashboard.css';

const ORGANIZER_HOME = ROUTES.PROFILE;

const NAV_ITEMS = [
  { id: 'home', label: 'Home', to: ORGANIZER_HOME, icon: IconHome, match: (pathname, search) => pathname === ORGANIZER_HOME && !search.includes('section=bookings') && !search.includes('section=account') },
  { id: 'discover', label: 'Discover', to: '/search-results', icon: IconSearch, match: (pathname) => pathname.startsWith('/search') || pathname.startsWith('/venues') },
  { id: 'bookings', label: 'Bookings', to: '/profile?section=bookings', icon: IconBookings, match: (pathname, search) => pathname === '/profile' && search.includes('section=bookings') },
];

const OrganizerHeader = () => {
  const location = useLocation();
  const { currentUser, userProfile, logout, isLoggingOut } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  const actorUserId =
    userProfile?.backend_user_id ||
    userProfile?.id ||
    userProfile?.firebase_uid ||
    currentUser?.uid ||
    null;

  useEffect(() => {
    let cancelled = false;
    if (!actorUserId) {
      setUnreadCount(0);
      return undefined;
    }

    const loadUnread = async () => {
      try {
        const threads = await messageThreadsApiService.listThreads({ userId: actorUserId, limit: 50 });
        if (cancelled) return;
        let readMap = getThreadReadMap(actorUserId);
        if (!hasThreadReadMap(actorUserId) && !threadListHasServerUnread(threads)) {
          readMap = buildReadMapFromThreads(threads);
          setThreadReadMap(actorUserId, readMap);
        }
        const count = countUnreadThreads(threads, readMap);
        setUnreadCount(count);
      } catch {
        if (!cancelled) setUnreadCount(0);
      }
    };

    loadUnread();
    const onReadUpdate = () => loadUnread();
    window.addEventListener('message-read-updated', onReadUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('message-read-updated', onReadUpdate);
    };
  }, [actorUserId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayName =
    userProfile?.first_name ||
    userProfile?.firstName ||
    (currentUser?.displayName ? currentUser.displayName.split(' ')[0] : null) ||
    (currentUser?.email ? currentUser.email.split('@')[0] : 'User');

  const fullName =
    [userProfile?.first_name || userProfile?.firstName, userProfile?.last_name || userProfile?.lastName]
      .filter(Boolean)
      .join(' ') ||
    currentUser?.displayName ||
    displayName;

  return (
    <>
      <header className="org-header">
        <Link to={ORGANIZER_HOME} className="org-header-logo" aria-label="Occasia home">
          <OccasiaLogoIcon className="org-header-logo-svg" aria-hidden="true" />
        </Link>

        <nav className="org-header-nav" aria-label="Organizer navigation">
          {NAV_ITEMS.map(({ id, label, to, icon: Icon, match }) => {
            const isActive = match(location.pathname, location.search);
            return (
              <Link
                key={id}
                to={to}
                className={`org-header-nav-link ${isActive ? 'org-header-nav-link--active' : ''}`}
              >
                <Icon className="org-header-nav-icon" />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="org-header-actions">
          <Link to="/messages" className="org-header-notification" aria-label="Messages">
            <IconNotification />
            {unreadCount > 0 ? (
              <span className="org-header-notification-badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            ) : null}
          </Link>

          <div className="org-header-user" ref={dropdownRef}>
            <button
              type="button"
              className="org-header-user-trigger"
              onClick={() => setIsDropdownOpen((open) => !open)}
              aria-expanded={isDropdownOpen}
              aria-haspopup="menu"
            >
              <span className="org-header-user-avatar" aria-hidden="true">
                {currentUser?.photoURL ? (
                  <img src={currentUser.photoURL} alt="" />
                ) : (
                  displayName.charAt(0).toUpperCase()
                )}
              </span>
              <span className="org-header-user-name">{fullName}</span>
              <IconArrowDown className={`org-header-user-chevron ${isDropdownOpen ? 'org-header-user-chevron--open' : ''}`} />
            </button>

            {isDropdownOpen ? (
              <div className="org-header-dropdown">
                <ProfileNavigation
                  variant="dropdown"
                  onNavigate={() => setIsDropdownOpen(false)}
                  onLogoutClick={() => {
                    setIsDropdownOpen(false);
                    setShowLogoutModal(true);
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={async () => {
          setShowLogoutModal(false);
          await logout();
        }}
        isLoggingOut={isLoggingOut}
      />
    </>
  );
};

export default OrganizerHeader;
